'use client'

import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/auth-context'
import {
    getWatchlistsAction,
    createWatchlistAction,
    deleteWatchlistAction,
} from '@/actions/watchlist'
import type { WatchlistWithEntities } from '@/types/watchlist'

interface WatchlistContextType {
    // Basic watchlists (without entities) - for sidebar
    watchlists: WatchlistWithEntities[]
    // Watchlists with entities - for watchlist page
    watchlistsWithEntities: WatchlistWithEntities[]
    // Loading states
    isLoadingBasic: boolean
    isLoadingWithEntities: boolean
    // Error states
    error: string | null
    // Methods
    fetchWatchlists: (options?: { includeEntities?: boolean; force?: boolean }) => Promise<void>
    createWatchlist: (name: string) => Promise<WatchlistWithEntities | null>
    deleteWatchlist: (watchlistId: string) => Promise<boolean>
    // Get watchlists based on what's needed
    getWatchlists: (includeEntities?: boolean) => WatchlistWithEntities[]
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined)

export function WatchlistProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth()
    const [watchlists, setWatchlists] = useState<WatchlistWithEntities[]>([])
    const [watchlistsWithEntities, setWatchlistsWithEntities] = useState<WatchlistWithEntities[]>(
        []
    )
    const [isLoadingBasic, setIsLoadingBasic] = useState(true)
    const [isLoadingWithEntities, setIsLoadingWithEntities] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Track what has been loaded to prevent redundant fetches
    const hasLoadedBasicRef = useRef(false)
    const hasLoadedWithEntitiesRef = useRef(false)
    const isLoadingBasicRef = useRef(false)
    const isLoadingWithEntitiesRef = useRef(false)

    // Fetch watchlists
    const fetchWatchlists = useCallback(
        async (options?: { includeEntities?: boolean; force?: boolean }) => {
            const { includeEntities = false, force = false } = options || {}

            if (!user?.id) return

            // Check if we should skip fetching
            if (!force) {
                if (includeEntities && hasLoadedWithEntitiesRef.current) return
                if (!includeEntities && hasLoadedBasicRef.current) return
            }

            // Prevent concurrent fetches
            if (includeEntities && isLoadingWithEntitiesRef.current) return
            if (!includeEntities && isLoadingBasicRef.current) return

            try {
                if (includeEntities) {
                    setIsLoadingWithEntities(true)
                    isLoadingWithEntitiesRef.current = true
                } else {
                    setIsLoadingBasic(true)
                    isLoadingBasicRef.current = true
                }

                setError(null)

                const { data, error: fetchError } = await getWatchlistsAction({
                    user_id: user.id,
                    include_entities: includeEntities,
                    include_details: false,
                })

                if (fetchError) {
                    setError(fetchError.message)
                    throw fetchError
                }

                if (includeEntities) {
                    setWatchlistsWithEntities(data || [])
                    hasLoadedWithEntitiesRef.current = true
                } else {
                    setWatchlists(data || [])
                    hasLoadedBasicRef.current = true
                }
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : 'Failed to load watchlists'
                setError(errorMessage)
            } finally {
                if (includeEntities) {
                    setIsLoadingWithEntities(false)
                    isLoadingWithEntitiesRef.current = false
                } else {
                    setIsLoadingBasic(false)
                    isLoadingBasicRef.current = false
                }
            }
        },
        [user?.id]
    )

    // Create watchlist
    const createWatchlist = useCallback(
        async (name: string): Promise<WatchlistWithEntities | null> => {
            if (!user?.id || !name.trim()) return null

            try {
                const { data, error: createError } = await createWatchlistAction(user.id, {
                    name: name.trim(),
                })

                if (createError || !data) {
                    setError(createError?.message || 'Failed to create watchlist')
                    return null
                }

                // Add to both lists
                setWatchlists((prev) => [data, ...prev])
                setWatchlistsWithEntities((prev) => [data, ...prev])

                // Dispatch event for other components
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                        new CustomEvent('watchlistCreated', {
                            detail: data,
                        })
                    )
                }

                return data
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : 'Failed to create watchlist'
                setError(errorMessage)
                return null
            }
        },
        [user?.id]
    )

    // Delete watchlist
    const deleteWatchlist = useCallback(async (watchlistId: string): Promise<boolean> => {
        if (!watchlistId) return false

        try {
            const { success, error: deleteError } = await deleteWatchlistAction(watchlistId)

            if (!success || deleteError) {
                setError(deleteError?.message || 'Failed to delete watchlist')
                return false
            }

            // Remove from both lists
            setWatchlists((prev) => prev.filter((w) => w.watchlist_id !== watchlistId))
            setWatchlistsWithEntities((prev) => prev.filter((w) => w.watchlist_id !== watchlistId))

            // Dispatch event for other components
            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('watchlistDeleted', {
                        detail: { watchlist_id: watchlistId },
                    })
                )
            }

            return true
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete watchlist'
            setError(errorMessage)
            return false
        }
    }, [])

    // Get watchlists based on what's needed
    const getWatchlists = useCallback(
        (includeEntities?: boolean): WatchlistWithEntities[] => {
            return includeEntities ? watchlistsWithEntities : watchlists
        },
        [watchlists, watchlistsWithEntities]
    )

    // Load basic watchlists when user is available
    useEffect(() => {
        if (user?.id && !hasLoadedBasicRef.current && !isLoadingBasicRef.current) {
            fetchWatchlists({ includeEntities: false })
        }
    }, [user?.id, fetchWatchlists])

    // Listen for external create/delete events to keep in sync
    useEffect(() => {
        const handleCreated = (e: Event) => {
            const custom = e as CustomEvent<WatchlistWithEntities>
            const data = custom.detail
            if (!data || !data.watchlist_id) return

            // Add to both lists if not already present
            setWatchlists((prev) => {
                if (prev.some((w) => w.watchlist_id === data.watchlist_id)) return prev
                return [data, ...prev]
            })
            setWatchlistsWithEntities((prev) => {
                if (prev.some((w) => w.watchlist_id === data.watchlist_id)) return prev
                return [data, ...prev]
            })
        }

        const handleDeleted = (e: Event) => {
            const custom = e as CustomEvent<{ watchlist_id: string }>
            const id = custom.detail?.watchlist_id
            if (!id) return

            // Remove from both lists
            setWatchlists((prev) => prev.filter((w) => w.watchlist_id !== id))
            setWatchlistsWithEntities((prev) => prev.filter((w) => w.watchlist_id !== id))
        }

        window.addEventListener('watchlistCreated', handleCreated)
        window.addEventListener('watchlistDeleted', handleDeleted)

        return () => {
            window.removeEventListener('watchlistCreated', handleCreated)
            window.removeEventListener('watchlistDeleted', handleDeleted)
        }
    }, [])

    // Memoize the context value
    const value: WatchlistContextType = useMemo(
        () => ({
            watchlists,
            watchlistsWithEntities,
            isLoadingBasic,
            isLoadingWithEntities,
            error,
            fetchWatchlists,
            createWatchlist,
            deleteWatchlist,
            getWatchlists,
        }),
        [
            watchlists,
            watchlistsWithEntities,
            isLoadingBasic,
            isLoadingWithEntities,
            error,
            fetchWatchlists,
            createWatchlist,
            deleteWatchlist,
            getWatchlists,
        ]
    )

    return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>
}

export function useWatchlist(): WatchlistContextType {
    const context = useContext(WatchlistContext)
    if (context === undefined) {
        throw new Error('useWatchlist must be used within a WatchlistProvider')
    }
    return context
}
